/* Native drawer handles opening, Escape, focus trap and scroll lock. */
(() => {
  if (customElements.get('peax-static-guide')) return;
  customElements.define('peax-static-guide', class extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
      this.onClick = event => {
        const tab = event.target.closest('[role="tab"]');
        if (this.tabs.includes(tab)) this.selectTab(this.tabs.indexOf(tab));
      };
      this.onKey = event => {
        const index = this.tabs.indexOf(event.target);
        if (index < 0) return;
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next !== undefined) {
          event.preventDefault();
          this.selectTab(next);
          this.tabs[next].focus();
        }
      };
      this.onChange = event => {
        if (event.target.matches('[data-psg-garment]')) this.selectGarment(event.target.value);
      };
      this.onBlockSelect = event => {
        const blockId = event.detail?.blockId;
        const reference = Array.from(this.querySelectorAll('[data-reference-id]')).find(el => el.dataset.referenceId === blockId);
        if (blockId !== this.dataset.guideBlock && !reference) return;
        if (reference) this.selectTab(2);
        const drawer = this.closest('drawer-element');
        const trigger = Array.from(document.querySelectorAll('button[aria-controls]')).find(el => el.getAttribute('aria-controls') === drawer.id && !drawer.contains(el));
        if (trigger && !drawer.classList.contains('is-open')) drawer.open({detail:{element:trigger}});
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
      this.addEventListener('change', this.onChange);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      const active = this.tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true');
      this.selectTab(Math.max(0, active));
      this.selectGarment(this.dataset.guideGarment || this.querySelector('[data-psg-garment]')?.value || 'jacket');
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKey);
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }
    selectTab(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        const panel = this.panels.find(el => el.id === tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = i !== index;
      });
    }
    selectGarment(garment) {
      this.querySelectorAll('[data-psg-chart]').forEach(chart => {
        chart.hidden = chart.dataset.psgChart !== garment;
      });
    }
  });
})();

/* Actual variant IDs only. One two-item request; never retry an uncertain write. */
(() => {
  if (customElements.get('peax-static-system')) return;
  let cartBusy = false;
  const kinds = ['jacket', 'pant'];
  const root = () => window.Shopify?.routes?.root || '/';
  const cartURL = () => window.theme?.routes?.cart_url || `${root()}cart`;
  const quantities = cart => {
    const counts = new Map();
    for (const item of cart.items || []) {
      const id = String(item.variant_id || item.id);
      counts.set(id, (counts.get(id) || 0) + item.quantity);
    }
    return counts;
  };
  async function requestJSON(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, {...options, signal:controller.signal});
      return {ok:response.ok, data:await response.json()};
    }
    finally { clearTimeout(timer); }
  }
  async function readCart() {
    const response = await requestJSON(`${root()}cart.js`, {headers:{Accept:'application/json'}, cache:'no-store'});
    if (!response.ok) throw new Error('Unable to verify your cart. No automatic retry was made.');
    return quantities(response.data);
  }
  customElements.define('peax-static-system', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-system-form]');
      this.button = this.querySelector('[data-system-add]');
      this.status = this.querySelector('[data-system-status]');
      this.cartLink = this.querySelector('[data-cart-link]');
      this.summary = this.querySelector('[data-system-summary]');
      this.label = this.label || this.button.textContent.trim() || 'Add to Cart';
      // Retain outcome locks if this same element reconnects during an editor move.
      this.pending = this.pending || false;
      this.uncertain = this.uncertain || false;
      this.completed = this.completed || false;
      try { this.data = JSON.parse(this.querySelector('[data-system-data]').textContent); }
      catch { this.status.textContent = 'This system is not available yet.'; return; }
      this.onSubmit = event => { event.preventDefault(); this.addSystem(); };
      this.form.addEventListener('submit', this.onSubmit);
      this.update();
    }
    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
    }
    selected(kind) {
      const id = String(this.data.configured[kind] || '').trim();
      return this.data[kind]?.variants.find(variant => String(variant.id) === id);
    }
    update() {
      const issues = [];
      const descriptions = [];
      for (const kind of kinds) {
        const product = this.data[kind];
        const variant = this.selected(kind);
        const name = kind === 'jacket' ? 'Jacket' : 'Pant';
        if (!product || !variant || product.requiresSellingPlan) {
          issues.push(`${name} is not available in this system yet.`);
        } else {
          descriptions.push(`${name}: ${variant.title}`);
          if (!variant.available) issues.push(`${name}: ${variant.title} is sold out.`);
          if (variant.minimum > 1 || variant.increment > 1) issues.push(`${name} cannot be purchased individually in this combination.`);
        }
      }
      if (this.data.jacket && this.data.pant && this.data.jacket.id === this.data.pant.id) issues.push('This system is unavailable.');
      this.valid = issues.length === 0;
      this.summary.textContent = descriptions.join(' · ');
      this.button.disabled = !this.valid || this.pending || this.uncertain || this.completed;
      if (!this.pending && !this.uncertain && !this.completed) this.status.textContent = issues.join(' ');
    }
    async addSystem() {
      if (this.pending || this.uncertain || this.completed || !this.valid) return;
      if (cartBusy) { this.status.textContent = 'Another system is being added. Please wait.'; return; }
      cartBusy = true;
      this.pending = true;
      this.form.setAttribute('aria-busy', 'true');
      this.button.disabled = true;
      this.button.textContent = 'ADDING…';
      this.status.textContent = 'Adding your selected jacket and pant…';
      const ids = kinds.map(kind => String(this.selected(kind).id));
      let before, attempted = false, rejected = false, responseError = '';
      try {
        before = await readCart();
        attempted = true;
        let response;
        try {
          response = await requestJSON(`${root()}cart/add.js`, {
            method:'POST', headers:{'Content-Type':'application/json', Accept:'application/json'},
            body:JSON.stringify({items:ids.map(id => ({id, quantity:1}))})
          });
          const data = response.data;
          if (!response.ok || data.status) { rejected = true; responseError = typeof data.description === 'string' ? data.description : 'Shopify could not add this combination.'; }
        } catch { responseError = 'The cart request did not return a confirmed result.'; }
        const after = await readCart();
        const changes = ids.map(id => (after.get(id) || 0) - (before.get(id) || 0));
        if (changes.every(quantity => quantity === 1)) {
          this.completed = true;
          this.status.textContent = 'Your selected jacket and pant were added.';
          this.cartLink.hidden = false;
          await this.showCart();
        } else if (changes.every(quantity => quantity === 0) && rejected) {
          this.status.textContent = `${responseError} Neither item was added. Please try again later or contact PEAX.`;
        } else {
          this.uncertain = true;
          this.cartLink.hidden = false;
          const detail = changes.map((quantity, i) => `${kinds[i]} quantity change: ${quantity >= 0 ? '+' : ''}${quantity}`).join('; ');
          this.status.textContent = `${responseError ? responseError + ' ' : ''}We could not confirm the complete system (${detail}). Review your cart before adding again. Existing items were not removed and no retry was made.`;
        }
      } catch {
        if (attempted) {
          this.uncertain = true;
          this.cartLink.hidden = false;
          this.status.textContent = 'We could not verify the cart after the request. Some items may have been added. Review your cart before adding again; no automatic retry was made.';
        } else this.status.textContent = 'We could not check your cart. No items were added. Please try again.';
      } finally {
        cartBusy = false;
        this.pending = false;
        this.form.setAttribute('aria-busy', 'false');
        this.button.textContent = this.completed ? 'ADDED TO CART' : this.label;
        this.button.disabled = !this.valid || this.uncertain || this.completed;
      }
    }
    async showCart() {
      const cart = document.querySelector('cart-element[data-cart-drawer]');
      if (!cart || window.theme?.settings?.cartType !== 'drawer') { window.location.assign(cartURL()); return; }
      const drawer = this.closest('drawer-element');
      if (drawer?.classList.contains('is-open')) {
        const closed = await new Promise(resolve => {
          let timer;
          const finish = success => { clearTimeout(timer); document.removeEventListener('theme:drawer:close', onClose); resolve(success); };
          const onClose = () => { if (!drawer.classList.contains('is-open')) setTimeout(() => finish(true), 0); };
          document.addEventListener('theme:drawer:close', onClose);
          timer = setTimeout(() => finish(false), 1200);
          drawer.close();
        });
        // Avoid overlapping focus traps if the theme's transition did not finish.
        if (!closed) { window.location.assign(cartURL()); return; }
      }
      const refreshed = new Promise(resolve => {
        let timer;
        const finish = result => { clearTimeout(timer); document.removeEventListener('theme:cart:added', onAdded); resolve(result); };
        const onAdded = () => finish(true);
        document.addEventListener('theme:cart:added', onAdded);
        timer = setTimeout(() => finish(false), 8000);
      });
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles:true}));
      if (!await refreshed) window.location.assign(cartURL());
    }
  });
})();
